import React, { useRef, useState } from 'react';
import { Document, PDFViewer, Font } from '@react-pdf/renderer';
import fontRegular from 'src/assets/fonts/Poppins-Regular.ttf';
import fontMedium from 'src/assets/fonts/Poppins-Medium.ttf';
import fontBold from 'src/assets/fonts/Poppins-Bold.ttf';
import { ControlsPdfQuery } from 'src/graphql/_generated';
import _ from 'lodash';
import { HtmlWrap } from '../comps';
import { ActionsConf, Toc, TocItem } from '../types';
import { RmpHead, RmpToc, RmpSummary, RmpRender, RmpEnd } from './comps';
import { IS_DEBUG } from '../conf';
import { auditEvent } from 'src/api/other';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { ColorSet } from 'src/redux/reducers/theme';
import { useOrganisationBranding } from 'src/hooks/useOrganisationBranding';

Font.register({
  family: 'Poppins',

  fonts: [
    {
      src: fontRegular,
      fontWeight: 400,
    },
    {
      src: fontMedium,
      fontWeight: 500,
    },
    {
      src: fontBold,
      fontWeight: 600,
    },
  ],
});

type ControlPdf = ControlsPdfQuery['controls'][number];

interface PdfRiskManagementProps {
  data: ControlPdf[];
  onClose: () => void;
  colors: ColorSet;
  includesActions: ActionsConf;
}

const PdfRiskManagement: React.FC<PdfRiskManagementProps> = ({ data, onClose, colors, includesActions }) => {
  const {
    logoUrl: brandingLogo,
    primaryColor: brandingPrimaryColor,
    secondaryColor: brandingSecondaryColor,
  } = useOrganisationBranding();
  const [tableOfContents, setTableOfContents] = useState<{
    toc: Toc[];
    hasPlanResults: boolean;
    hasControlsResults: boolean;
  }>({
    toc: [
      { title: 'Risk Management Plan', pageNumber: -1, id: 'rmp' },
      {
        title: 'Controls',
        pageNumber: -1,
        id: data[0].name.toLowerCase(),
        children: data.map((el) => {
          return {
            title: el.name,
            pageNumber: 0,
          };
        }),
      },
    ],
    hasPlanResults: false,
    hasControlsResults: false,
  });

  const [renderCount, setRenderCount] = useState(0);
  const isPrinting = useRef(false);

  const onRmpSummaryDone = (pageNumber: number) => {
    if (tableOfContents.hasPlanResults) return;

    const contents = _.cloneDeep(tableOfContents.toc) || [];
    const newContents = [contents[0], contents[1]];

    newContents[0].pageNumber = pageNumber;

    setTableOfContents({
      toc: newContents,
      hasPlanResults: true,
      hasControlsResults: false,
    });
  };

  const onControlsDone = (data: TocItem[]) => {
    if (tableOfContents?.hasControlsResults) return;
    const contents = _.cloneDeep(tableOfContents?.toc) || [];
    const newContents = [contents[0]];

    if (data.length) {
      newContents.push({ title: 'Controls', pageNumber: data[0].pageNumber, id: data[0].title, children: data });
    }

    setTableOfContents({
      hasPlanResults: true,
      hasControlsResults: true,
      toc: newContents,
    });
  };

  // FILE DOWNLOAD
  const print = (blob: Blob | undefined) => {
    if (blob) {
      let elm = document.createElement('a');
      elm.href = URL.createObjectURL(blob);
      elm.setAttribute('download', 'Risk Management Plan.pdf');
      elm.click();
      elm.remove();

      auditEvent({
        action: 'ExportPdf',
        resource: 'control',
        control_id: data.map((el) => el.id),
      }).then(() => queryCache.invalidateQueries([QKeys.Audit]));

      onClose();
    }
  };

  if (!data) onClose();

  return (
    <HtmlWrap $isTest={IS_DEBUG}>
      <PDFViewer>
        <Document
          onRender={(el) => {
            if (renderCount === 4 && !isPrinting.current) {
              if (!IS_DEBUG) {
                print(el.blob);
                isPrinting.current = true;
              }
            } else {
              setRenderCount((s) => s + 1);
            }
          }}
        >
          <RmpHead
            numberOfStrategies={data.length}
            colors={colors}
            logoSrc={brandingLogo}
            primaryColor={brandingPrimaryColor}
            secondaryColor={brandingSecondaryColor}
          />
          <RmpToc
            contents={tableOfContents.toc}
            colors={colors}
            logoSrc={brandingLogo}
            primaryColor={brandingPrimaryColor}
          />
          <RmpSummary
            onDone={onRmpSummaryDone}
            data={data}
            colors={colors}
            logoSrc={brandingLogo}
            secondaryColor={brandingSecondaryColor}
          />
          {tableOfContents.hasPlanResults && (
            <RmpRender
              controls={data}
              onDone={onControlsDone}
              colors={colors}
              includesActions={includesActions}
              logoSrc={brandingLogo}
              secondaryColor={brandingSecondaryColor}
            />
          )}
          <RmpEnd
            companyImage={''}
            colors={colors}
            logoSrc={brandingLogo}
            primaryColor={brandingPrimaryColor}
            secondaryColor={brandingSecondaryColor}
          />
        </Document>
      </PDFViewer>
    </HtmlWrap>
  );
};

export default PdfRiskManagement;
